import { useCallback, useMemo } from "react";

import { isEqual } from "lodash";
import {
  type QueryParamConfig,
  useQueryParam,
  withDefault,
} from "use-query-params";

import { CustomJsonCompressedParam } from "@north-beam/nb-common";
import { type CategoricalBreakdownConfig } from "@north-beam/nb-common";

import {
  addNewEmptyChart,
  updateChartView,
  updateChartType,
  updateAlignAxes,
  removeLastChart,
  addCompareToChartAt,
  deleteCompareFromChartAt,
  updateMainMetricFromChartAt,
  getCompareAt,
  updateCompareFromChartAt,
  removeAllCompareDateRangeFromCharts,
  removeAllMetricFiltersFromChartAt,
  updateZoom,
} from "../utils/chart-state-helpers";
import { type CompareQuery } from "../utils/compare-query";

export type MetricFilters = {
  breakdown: CategoricalBreakdownConfig;
  values: CategoricalBreakdownConfig["choices"][number][];
};

type ChartMetric = {
  metricName: string;
  filters: MetricFilters[];
  ignoreTableFilters: boolean;
};
export type MainMetric = ChartMetric;
export type Compare = {
  filters: MetricFilters[];
  ignoreTableFilters: boolean;
} & CompareQuery;
export type ChartConfig = {
  chartView: "aggregate" | "split";
  chartType: "line" | "bar";
  alignAxesScale: boolean;
  mainMetric: MainMetric;
  comparisons: Compare[];
  zoomEnabled: boolean;
};
export type ChartState = {
  chartConfigs: ChartConfig[];
};

type ChartStateParam = ChartState;
const DEFAULT_CHART_STATE: ChartState = {
  chartConfigs: [
    {
      chartView: "aggregate",
      chartType: "line",
      alignAxesScale: false,
      mainMetric: {
        metricName: "spend",
        filters: [],
        ignoreTableFilters: false,
      },
      comparisons: [],
      zoomEnabled: false,
    },
  ],
};
export const isDefaultChartState = (chartState: ChartState) =>
  isEqual(chartState, DEFAULT_CHART_STATE);

export const ChartStateJsonParam: QueryParamConfig<ChartStateParam> =
  withDefault(
    CustomJsonCompressedParam<ChartStateParam>(),
    DEFAULT_CHART_STATE,
  );

export const useChartState = () => {
  const [chartState, setChartState] = useQueryParam<ChartStateParam>(
    "chartState",
    ChartStateJsonParam,
    { updateType: "pushIn" },
  );

  return {
    chartState,
    chartConfigs: chartState.chartConfigs,
    setChartState,
    getChartAt: useCallback(
      (chartIndex: number) => chartState.chartConfigs[chartIndex],
      [chartState.chartConfigs],
    ),
    updateZoom: useCallback(
      (...args: Parameters<typeof updateZoom>) => {
        setChartState(updateZoom(...args), "pushIn");
      },
      [setChartState],
    ),
    addChart: useCallback(
      () => setChartState(addNewEmptyChart, "pushIn"),
      [setChartState],
    ),
    removeChart: useCallback(
      () => setChartState(removeLastChart, "pushIn"),
      [setChartState],
    ),
    updateChartType: useCallback(
      (...args: Parameters<typeof updateChartType>) => {
        setChartState(updateChartType(...args), "pushIn");
      },
      [setChartState],
    ),
    updateAlignAxes: useCallback(
      (...args: Parameters<typeof updateAlignAxes>) => {
        setChartState(updateAlignAxes(...args), "pushIn");
      },
      [setChartState],
    ),
    updateChartView: useCallback(
      (...args: Parameters<typeof updateChartView>) =>
        setChartState(updateChartView(...args), "pushIn"),
      [setChartState],
    ),
    addCompareToChartAt: useCallback(
      (...args: Parameters<typeof addCompareToChartAt>) =>
        setChartState(addCompareToChartAt(...args), "pushIn"),
      [setChartState],
    ),
    deleteCompareFromChartAt: useCallback(
      (...args: Parameters<typeof deleteCompareFromChartAt>) =>
        setChartState(deleteCompareFromChartAt(...args), "pushIn"),
      [setChartState],
    ),
    updateMainMetricFromChartAt: useCallback(
      (...args: Parameters<typeof updateMainMetricFromChartAt>) =>
        setChartState(updateMainMetricFromChartAt(...args), "pushIn"),
      [setChartState],
    ),
    getCompareAt: useCallback(
      (...args: Parameters<ReturnType<typeof getCompareAt>>) =>
        getCompareAt(chartState)(...args),
      [chartState],
    ),
    updateCompareFromChartAt: useCallback(
      (...args: Parameters<typeof updateCompareFromChartAt>) =>
        setChartState(updateCompareFromChartAt(...args), "pushIn"),
      [setChartState],
    ),
    removeAllCompareDateRangeFromCharts: useCallback(
      (...args: Parameters<typeof removeAllCompareDateRangeFromCharts>) =>
        setChartState(removeAllCompareDateRangeFromCharts(...args), "pushIn"),
      [setChartState],
    ),
    chartCount: useMemo(
      () => chartState.chartConfigs.length,
      [chartState.chartConfigs.length],
    ),
    removeAllMetricFiltersFromChartAt: useCallback(
      (...args: Parameters<typeof removeAllMetricFiltersFromChartAt>) =>
        setChartState(removeAllMetricFiltersFromChartAt(...args), "pushIn"),
      [setChartState],
    ),
  };
};
